import cx from 'classnames';
import { isUndefined } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Card } from 'components/Card';
import { Icon } from 'components/Icon';
import { Heading } from 'components/Typography';
import { Appointment } from 'domains/appointments/types';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { DATE_LONG_DAY_MONTH_YEAR, TIME_ONLY } from 'utils/date';

type CancellationCardProps = {
  appointment: Appointment;
  className?: string;
};

export const AppointmentCancellationCard: React.FC<CancellationCardProps> = ({
  appointment: {
    therapistCredentials,
    appointmentWith: therapistName,
    appointmentType,
    appointmentDuration,
    appointmentDateTime,
  },
  className,
}) => {
  const { format, displayTimezone } = useDisplayTimezone();
  const { t } = useTranslation();

  if (isUndefined(displayTimezone)) {
    throw new Error('Undefined display timezone when confirming appointment date');
  }

  return (
    <Card className={className}>
      <Heading level="h6" className="mb-40 sm-d:mb-32">
        {t(`cancellationCard.title`)}
      </Heading>
      <div className="flex justify-between items-center mb-16">
        <Heading level="h5" className="font-semibold">
          {t('cancellationCard.appointment.title', {
            type: appointmentType,
            duration: appointmentDuration,
          })}
        </Heading>
      </div>
      <div className={cx('flex flex-col gap-8')}>
        <div className="flex gap-12 items-center">
          <Icon name="Calendar" className="shrink-0" />
          <Heading level="h8" className="font-medium">
            {t('cancellationCard.appointment.date', {
              date: format(appointmentDateTime, DATE_LONG_DAY_MONTH_YEAR),
              therapist: `${therapistName}, ${therapistCredentials}`,
            })}
          </Heading>
        </div>
        <div className="flex gap-12 items-center">
          <Icon name="Clock" className="shrink-0" />
          <Heading level="h8" className="font-medium">
            {`${format(appointmentDateTime, TIME_ONLY)} ${displayTimezone.label}`}
          </Heading>
        </div>
      </div>
    </Card>
  );
};
